import { useRef, useState } from "react";

import CuoButton from "@/app/admin/_common/CuoButton";
import { Label } from "@/app/admin/_common/ui/label";
import { Textarea } from "@/app/admin/_common/ui/textarea";
import type {
  AutomationTemplate,
  ContextField,
} from "@/app/_services/AutomationService";

import VariableList from "./VariableList";

export default function SmsTemplateEditor({
  template,
  contextFields,
  onSave,
}: {
  template: AutomationTemplate;
  contextFields: ContextField[];
  onSave: (body: { key: string; body?: string }) => Promise<boolean>;
}) {
  const [draft, setDraft] = useState<{ body?: string }>({});
  const [isSaving, setIsSaving] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const isDirty = Object.keys(draft).length > 0;
  const body = draft.body ?? template.body ?? "";

  async function handleSave() {
    setIsSaving(true);
    try {
      const ok = await onSave({ key: template.key, ...draft });
      if (ok) setDraft({});
    } finally {
      setIsSaving(false);
    }
  }

  function insertVariable(path: string) {
    const token = `{{${path}}}`;
    const el = bodyRef.current;
    // Append when the textarea has never been focused and so has no caret position.
    const start = el?.selectionStart ?? body.length;
    const end = el?.selectionEnd ?? body.length;
    const next = body.slice(0, start) + token + body.slice(end);

    setDraft({ body: next });

    // The caret has to be restored after React writes the new value, or it jumps to the
    // end and the next insert lands in the wrong place.
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 py-5">
      <div className="mx-auto flex w-full max-w-[920px] flex-col gap-4 pb-24">
        <div className="flex flex-col gap-3 rounded-md border border-border bg-card p-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="template-body" className="text-[12.5px]">
              Message
            </Label>
            <Textarea
              id="template-body"
              ref={bodyRef}
              rows={8}
              value={body}
              onChange={(e) => setDraft({ body: e.target.value })}
            />
          </div>

          {/* No subject and no rich editing: send_sms reads `body` only, as plain text. */}
          <p className="text-[11.5px] text-muted-foreground">
            Plain text. Placeholders use the same paths conditions do.
          </p>
        </div>

        {contextFields.length > 0 && (
          <div className="flex flex-col gap-3 rounded-md border border-border bg-muted/30 p-4">
            <span className="text-[12.5px] font-medium text-foreground">
              Insert a variable
            </span>
            <VariableList contextFields={contextFields} onPick={insertVariable} />
          </div>
        )}

        {isDirty && (
          <div className="sticky bottom-5 z-10 flex w-full items-center justify-between gap-3 rounded-md border border-border bg-card px-5 py-3">
            <span className="text-[12.5px] text-muted-foreground">
              You have unsaved changes
            </span>
            <div className="flex items-center gap-2">
              <CuoButton
                variant="outline"
                label="Discard"
                onClick={() => setDraft({})}
              />
              <CuoButton label="Save" disableOnAsync={isSaving} onClick={handleSave} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
